import { useEffect, useState } from 'react'
import { Upload, Check, ImageOff, Loader2, Download } from 'lucide-react'
import { api, blobURL, mediaURL, ApiError } from './api'
import type { UploadProgress } from './api'
import type { UploadResult } from './types'
import { cn, wordCount, bytes, duration } from './lib/utils'
import { Button, Skeleton } from './components/ui'

export { usd } from './lib/utils'

/** Loads a workspace file through fetch, because a bare <img src> cannot carry
 *  the Authorization header the file endpoint requires. */
export function AuthImage({ ws, storageKey, alt, className, ratio = 'square', thumbnail = false }: {
  ws: string
  storageKey: string | null | undefined
  alt: string
  className?: string
  ratio?: 'square' | 'portrait' | 'free'
  thumbnail?: boolean
}) {
  const [url, setUrl] = useState('')
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!storageKey) return
    setUrl(''); setFailed(false)
    let dead = false
    let made = ''
    blobURL(ws, storageKey, thumbnail)
      .then(u => { if (dead) { URL.revokeObjectURL(u); return } made = u; setUrl(u) })
      .catch(() => setFailed(true))
    // Object URLs are never garbage collected on their own; without this the
    // browser holds every image the session has ever shown.
    return () => { dead = true; if (made) URL.revokeObjectURL(made) }
  }, [ws, storageKey, thumbnail])

  const shape = ratio === 'portrait' ? 'aspect-[3/4]' : ratio === 'square' ? 'aspect-square' : ''
  const base = cn('overflow-hidden rounded-[--radius-control] bg-surface-2', shape, className)

  if (!storageKey || failed) {
    return (
      <div className={cn(base, 'grid place-items-center border border-line text-ink-faint')}>
        <ImageOff className="size-4" />
      </div>
    )
  }
  if (!url) return <Skeleton className={base} />
  return <img src={url} alt={alt} className={cn(base, 'object-cover')} />
}

/** Starts an authenticated browser download without first buffering the whole
 * video in JavaScript. Finished ads are tens of megabytes, so a blob download
 * would appear to do nothing until every byte had already arrived. */
export function DownloadFileButton({
  ws, storageKey, filename, size = 'sm', variant = 'secondary', className,
}: {
  ws: string
  storageKey: string
  filename: string
  size?: 'sm' | 'md'
  variant?: 'primary' | 'secondary'
  className?: string
}) {
  const [busy, setBusy] = useState(false)

  async function download() {
    setBusy(true)
    try {
      const url = await mediaURL(ws, storageKey)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={download}
      disabled={busy}
    >
      {busy ? <Loader2 className="animate-spin" /> : <Download />}
      {busy ? 'Preparing…' : 'Download'}
    </Button>
  )
}

/** Uploads on selection rather than on submit, so a large transfer happens
 *  while the rest of the form is being filled in, and a rejected file is
 *  reported before anything else has been typed. */
export function FilePicker({ ws, category, accept, label, value, onChange, hint }: {
  ws: string
  category: string
  accept: string
  label: string
  value: string
  onChange: (key: string, meta: UploadResult) => void
  hint?: React.ReactNode
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [progress, setProgress] = useState<UploadProgress | null>(null)

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // so picking the same file again re-fires change
    if (!file) return

    setBusy(true); setError(''); setProgress(null); setName(file.name)
    try {
      const res = await api.upload<UploadResult>(
        `/api/workspaces/${ws}/files?category=${category}`, file, setProgress)
      setName(res.filename)
      onChange(res.key, res)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload failed')
      setName('')
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  const done = !!value && !busy
  const pct = progress?.fraction != null ? Math.round(progress.fraction * 100) : null

  return (
    <div className="space-y-1.5">
      <label
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-[--radius-control] border border-dashed px-4 py-3.5 transition-colors',
          done ? 'border-good/40 bg-good/5'
            : busy ? 'border-brand/40 bg-brand/[0.04] cursor-default'
              : 'border-line hover:border-line-strong hover:bg-surface-2/50',
        )}
      >
        <span
          className={cn(
            'grid size-9 shrink-0 place-items-center rounded-full',
            done ? 'bg-good/15 text-good'
              : busy ? 'bg-brand/15 text-brand'
                : 'bg-surface-2 text-ink-faint',
          )}
        >
          {done ? <Check className="size-4" />
            : busy ? <Loader2 className="size-4 animate-spin" />
              : <Upload className="size-4" />}
        </span>

        <span className="min-w-0 flex-1 space-y-1.5">
          <span className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-medium text-ink">{label}</span>
            {busy && (
              <span className="shrink-0 text-xs tabular-nums text-brand">
                {pct != null ? `${pct}%` : 'Processing…'}
              </span>
            )}
          </span>

          {busy ? (
            <>
              {/* A determinate bar while bytes are moving, an indeterminate
                  sweep once they have all arrived -- the server still has to
                  store and probe the file, and a bar parked at 100% reads as
                  a hang. */}
              <span className="block h-1 overflow-hidden rounded-full bg-surface-3">
                {pct != null ? (
                  <span
                    className="block h-full rounded-full bg-brand transition-[width] duration-200"
                    style={{ width: `${pct}%` }}
                  />
                ) : (
                  <span className="shimmer block h-full w-full rounded-full" />
                )}
              </span>
              <span className="block truncate text-xs text-ink-faint">
                {progress && pct != null
                  ? `${bytes(progress.loaded)} of ${bytes(progress.total)}` +
                    (progress.bytesPerSecond > 0 ? ` · ${bytes(progress.bytesPerSecond)}/s` : '') +
                    (progress.secondsRemaining != null && progress.secondsRemaining > 2
                      ? ` · ${duration(progress.secondsRemaining)} left`
                      : '')
                  : `${name} — finishing up`}
              </span>
            </>
          ) : (
            <span className="block truncate text-xs text-ink-faint">
              {done ? name || 'Uploaded' : 'Click to choose a file'}
            </span>
          )}
        </span>

        <input type="file" accept={accept} onChange={pick} disabled={busy} className="sr-only" />
      </label>

      {hint && !error && !busy && (
        <p className="text-xs leading-relaxed text-ink-faint">{hint}</p>
      )}
      {error && <p className="text-xs text-bad">{error}</p>}
    </div>
  )
}

/** Word count against the calibrated target.
 *
 *  Shown while writing rather than at submit because the model paces delivery
 *  to FILL the clip: an underfilled line comes back padded and slow, and the
 *  fix is to ADD words -- the opposite of what most people reach for when a
 *  take sounds sluggish. */
export function WordCount({ text }: { text: string }) {
  const n = wordCount(text)
  if (n === 0) return null
  const ok = n >= 18 && n <= 28
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={cn('font-medium tabular-nums', ok ? 'text-good' : 'text-warn')}>
        {n} words
      </span>
      {!ok && (
        <span className="text-ink-faint">
          {n < 18
            ? 'aim for ~23 — short lines come back padded and slow'
            : 'aim for ~23 — long lines get rushed'}
        </span>
      )}
      <span className="ml-auto h-1 w-24 overflow-hidden rounded-full bg-surface-2">
        <span
          className={cn('block h-full rounded-full transition-all', ok ? 'bg-good' : 'bg-warn')}
          style={{ width: `${Math.min(100, (n / 28) * 100)}%` }}
        />
      </span>
    </div>
  )
}
